(() => {
  const header = document.querySelector('[data-opsin-header]');
  if (!header) return;

  // The drawer and backdrop are siblings of the header (see opsin-header.liquid
  // for why), so they are queried at document level, not inside the header.
  const openButton = header.querySelector('[data-opsin-menu-open]');
  const menu = document.querySelector('[data-opsin-mobile-menu]');
  const backdrop = document.querySelector('[data-opsin-menu-backdrop]');
  const closeButton = menu ? menu.querySelector('[data-opsin-menu-close]') : null;
  let previousFocus = null;

  if (!openButton || !menu) return;

  const focusable = () => Array.from(menu.querySelectorAll('a[href], button:not([disabled])'));

  const closeMenu = () => {
    menu.hidden = true;
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove('opsin-menu-open');
    openButton.setAttribute('aria-expanded', 'false');
    if (previousFocus) previousFocus.focus();
  };

  const openMenu = () => {
    previousFocus = document.activeElement;
    menu.hidden = false;
    if (backdrop) backdrop.hidden = false;
    document.body.classList.add('opsin-menu-open');
    openButton.setAttribute('aria-expanded', 'true');
    const items = focusable();
    if (items.length) items[0].focus();
  };

  openButton.addEventListener('click', openMenu);
  if (closeButton) closeButton.addEventListener('click', closeMenu);
  if (backdrop) backdrop.addEventListener('click', closeMenu);

  document.addEventListener('keydown', (event) => {
    if (menu.hidden) return;
    if (event.key === 'Escape') closeMenu();
    if (event.key !== 'Tab') return;
    const items = focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* Homepage review carousel controls (P4, 2026-08-30): the review grid is a
   CSS scroll-snap carousel below 768px (see opsin-fixes.css). This adds real
   prev/next buttons and a "1 / 3" position indicator. Injected only when the
   carousel layout is active; swipe alone works without it. No autoplay. */
(() => {
  const grid = document.querySelector('.opsin-proof__grid');
  if (!grid) return;
  const cards = Array.from(grid.querySelectorAll('.opsin-review-card'));
  if (cards.length < 2) return;
  const mq = window.matchMedia('(max-width: 767.98px)');

  let nav = null;
  const build = () => {
    if (nav || !mq.matches) return;
    nav = document.createElement('div');
    nav.className = 'opsin-proof__carousel-nav';
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.setAttribute('aria-label', 'Previous review');
    prev.textContent = '←';
    const count = document.createElement('span');
    count.className = 'opsin-proof__carousel-count';
    count.setAttribute('aria-live', 'polite');
    const next = document.createElement('button');
    next.type = 'button';
    next.setAttribute('aria-label', 'Next review');
    next.textContent = '→';
    nav.append(prev, count, next);
    grid.after(nav);

    const current = () => {
      const mid = grid.scrollLeft + grid.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const center = c.offsetLeft + c.offsetWidth / 2;
        const d = Math.abs(center - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    };
    const update = () => { count.textContent = (current() + 1) + ' / ' + cards.length; };
    const goTo = (i) => {
      const c = cards[Math.max(0, Math.min(cards.length - 1, i))];
      grid.scrollTo({ left: c.offsetLeft - (grid.clientWidth - c.offsetWidth) / 2 });
    };
    prev.addEventListener('click', () => goTo(current() - 1));
    next.addEventListener('click', () => goTo(current() + 1));
    grid.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    update();
  };
  build();
  mq.addEventListener('change', () => { if (mq.matches) build(); else if (nav) { nav.remove(); nav = null; } });
})();

/* Big Game sticky-bar activation (2026-08-30): the Big Game section's own
   mobile sticky bar previously showed at page load. Apply the same rule as
   the standard PDP bar — hidden until the buy module has been seen AND
   scrolled past, hidden again when the shopper returns to it. Pairs with
   the .is-active gate in opsin-fixes.css. Fail-safe: without
   IntersectionObserver the bar never shows. */
(() => {
  const section = document.querySelector('.opsin-big-game');
  if (!section || !('IntersectionObserver' in window)) return;
  const stickyBar = section.querySelector('[data-opsin-sticky]');
  const buyModule = section.querySelector('.opsin-buy');
  if (!stickyBar || !buyModule) return;

  let seenBuy = false;
  let pastBuy = false;
  const io = new IntersectionObserver((entries) => {
    const entry = entries[0];
    if (entry.isIntersecting) {
      seenBuy = true;
      pastBuy = false;
    } else if (seenBuy) {
      pastBuy = entry.boundingClientRect.bottom < 0;
    }
    stickyBar.classList.toggle('is-active', seenBuy && pastBuy);
  }, { threshold: 0 });
  io.observe(buyModule);
})();

/* PDP diameter chart axis (2026-10-24): the description HTML ships every bar
   on a fixed 0–1.0 mm axis, which squashes 0.37 vs 0.46 into the left third.
   Greg's spec: Opsin's bar at ~25% of the track, the widest competitor at
   ~90%. That is a zoomed comparison scale (the axis does not start at 0), so
   the mapping is disclosed three ways — the axis end labels show the real mm
   at each end, the subheading names the range, and a note under the axis
   says the scale is zoomed. The mm values and % callouts in each row are the
   single source of truth and are never touched; only bar length is derived.
   Bars still animate from zero via scaleX (see the chart's own <style>), so
   changing width before the chart scrolls into view is invisible. Competitor
   bars pick up the hue of their own % callout so the gradient reads as
   "further from Opsin = warmer" — the number carries the meaning, the colour
   only reinforces it. No-op on pages without #opsin-chart. */
(() => {
  const chart = document.getElementById('opsin-chart');
  if (!chart) return;
  const rows = Array.from(chart.querySelectorAll('.opsin-fill')).map((fill) => {
    const row = fill.closest('div[style*="margin:0 0"]');
    const m = row && row.textContent.match(/(\d\.\d{2,3})\s*mm/);
    return m ? { fill, row, mm: parseFloat(m[1]) } : null;
  }).filter(Boolean);
  if (rows.length < 2) return;
  const opsinRow = rows.find((r) => r.row.querySelector('.opsin-dot')) || rows[0];
  const lo = opsinRow.mm;
  const hi = Math.max(...rows.map((r) => r.mm));
  if (!(hi > lo)) return;
  const LO_PCT = 25;
  const HI_PCT = 90;
  const perMm = (HI_PCT - LO_PCT) / (hi - lo);
  const axisMin = lo - LO_PCT / perMm;
  const axisMax = axisMin + 100 / perMm;
  const fmt = (v) => v.toFixed(2);
  rows.forEach((r) => {
    const pct = (LO_PCT + (r.mm - lo) * perMm).toFixed(1).replace(/\.0$/, '') + '%';
    r.fill.style.width = pct;
    const dot = r.row.querySelector('.opsin-dot');
    if (dot) dot.style.left = 'calc(' + pct + ' - 7px)';
    if (r !== opsinRow) {
      const callout = r.row.querySelector('strong');
      const hue = callout && callout.style.color;
      if (hue) r.fill.style.background = 'linear-gradient(90deg,#8E8E88 0%,#B3B0A8 55%,' + hue + ' 100%)';
    }
  });
  let axisRow = null;
  chart.querySelectorAll('span').forEach((s) => {
    if (s.children.length !== 0) return;
    const t = s.textContent.trim();
    if (t === '0 mm') { s.textContent = fmt(axisMin) + ' mm'; axisRow = s.parentElement; }
    else if (t === '1.0 mm') { s.textContent = fmt(axisMax) + ' mm'; }
  });
  const range = fmt(axisMin) + '–' + fmt(axisMax) + ' mm';
  chart.querySelectorAll('div').forEach((d) => {
    if (d.children.length !== 0) return;
    const t = d.textContent.trim();
    if (t.indexOf('clearly labeled 0–1.0 mm scale') !== -1) {
      d.textContent = t.replace('clearly labeled 0–1.0 mm scale', 'zoomed ' + range + ' comparison scale');
    } else if (/^Published \d+ lb leader diameters\.$/.test(t)) {
      d.textContent = t.replace(/\.$/, ' on a zoomed ' + range + ' comparison scale.');
    }
  });
  if (axisRow && !chart.querySelector('.opsin-scale-note')) {
    const note = document.createElement('div');
    note.className = 'opsin-scale-note';
    note.textContent = 'Zoomed to show separation: the track runs ' + range + ', not from 0. Values are published diameters.';
    axisRow.after(note);
  }
})();
